import { NavLink } from 'react-router-dom';

import type { AdminNavItem, AdminNavSection, AdminNavigation } from '../../types/admin';
import { JCONSULT_LOGO_WHITE } from '../../config/brandingAssets';
import { usePublicBranding } from '../../hooks/usePublicBranding';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';

interface SidebarProps {
  items: AdminNavigation;
  onCloseMobile: () => void;
  brandAlt?: string;
}

function normalizeSections(navigation: AdminNavigation): AdminNavSection[] {
  if (navigation.length === 0) return [];
  const first = navigation[0];
  if (first && 'items' in first) {
    return navigation as AdminNavSection[];
  }
  return [{ id: 'main', items: navigation as AdminNavItem[] }];
}

/** Accueil `/app` ne doit pas rester actif sur `/app/formations`, etc. */
function navItemRequiresExactMatch(itemPath: string, allItems: AdminNavItem[]) {
  return allItems.some(
    (other) => other.path !== itemPath && other.path.startsWith(`${itemPath}/`),
  );
}

export default function Sidebar({
  items,
  onCloseMobile,
  brandAlt = 'Logo',
}: SidebarProps) {
  const sections = normalizeSections(items);
  const allItems = sections.flatMap((section) => section.items);
  const { supportEmail } = usePublicBranding();
  const supportMailto = `mailto:${supportEmail}`;

  return (
    <aside className="flex h-full w-full flex-col bg-primary-dark p-6 text-white">
      <div className="relative mb-3 flex shrink-0 justify-center">
        <img
          src={JCONSULT_LOGO_WHITE}
          alt={brandAlt}
          className="h-auto max-w-[168px] object-contain"
        />
        <button
          type="button"
          className="absolute right-0 top-0 rounded-lg p-1 text-white/80 hover:bg-white/10 lg:hidden"
          onClick={onCloseMobile}
          aria-label="Fermer le menu"
        >
          <LucideSymbolIcon name="close" tone="inherit" className="text-white" size={20} />
        </button>
      </div>

      <div className="admin-sidebar-scroll no-scrollbar min-h-0 flex-1">
        <nav className="space-y-0" aria-label="Navigation principale">
          {sections.map((section, sectionIndex) => (
            <div
              key={section.id}
              className={sectionIndex > 0 ? 'mt-4 border-t border-white/10 pt-4' : undefined}
            >
              {section.label ? (
                <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-widest text-white/45">
                  {section.label}
                </p>
              ) : null}
              <div className="space-y-1.5">
                {section.items.map((item) => (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    end={navItemRequiresExactMatch(item.path, allItems)}
                    onClick={onCloseMobile}
                    className={({ isActive }) =>
                      [
                        'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-200',
                        isActive ? 'admin-nav-active' : 'text-white/90 hover:bg-white/10',
                      ].join(' ')
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <LucideSymbolIcon
                          name={item.icon}
                          tone="inherit"
                          className={isActive ? 'text-primary' : 'text-white'}
                          size={20}
                          filled={isActive && item.icon === 'dashboard'}
                        />
                        <span>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="mt-6 border-t border-white/10 pt-5 pb-1">
          <div className="rounded-xl border border-white/10 bg-white/10 p-4 text-center">
            <p className="mb-3 text-sm font-medium">Besoin d&apos;aide sur la plateforme ?</p>
            <a
              href={supportMailto}
              className="inline-flex w-full items-center justify-center rounded-lg bg-white py-2.5 text-sm font-bold text-primary shadow-md transition-transform hover:scale-[1.02]"
            >
              Contacter le support
            </a>
          </div>
        </div>
      </div>
    </aside>
  );
}
